'use client';

import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';

// Actions
import { getUserProfile } from '@/actions/client/user';

// Utils
import { showErrorToast } from '@/utils/errorHandlers';
import { setUserRole } from '@/utils/cookie';
import { cookieStorage } from '@shared/utils';
import { clearAllChatbotThreadIds } from '@/utils/chat';

// Constants
import { AUTH_ERROR_MESSAGES } from '@/constants/auth';
import { ROUTES } from '@shared/constants';

// Store
import { appStoreActions, useSetAuthorized } from '@/stores';

// Constants
import { EVENT } from '@/constants/event';

// Providers
import { useFirebase } from './firebase';
export interface AuthContextType {
  isLoading: boolean;
  handleSignOut: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
interface AuthProviderProps {
  children: ReactNode;
}
export const AuthProvider = ({
  children
}: AuthProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const {
    signOut,
    getToken,
    isLoading: isFirebaseLoading
  } = useFirebase();
  const setAuthorized = useSetAuthorized();
  const handleSignOut = useCallback(async () => {
    await signOut();

    // Dispatch logout event for chat cleanup
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(EVENT.USER_LOGOUT));
    }

    // Clear thread IDs of chatbot
    clearAllChatbotThreadIds();

    // Clear cookies
    cookieStorage.clearAllItems();

    // Clear all stores
    appStoreActions.clearAllStores();

    // Use window.location.replace to clear all browser history
    // This prevents users from going back to protected pages even with multiple back clicks
    window.location.replace(ROUTES.SIGN_IN);
  }, [signOut]);
  const validateSession = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getToken();

      // Call API to validate session and get role
      const {
        data,
        error
      } = await getUserProfile({
        requestConfig: {
          token
        }
      });
      if (error) {
        if (error === AUTH_ERROR_MESSAGES.ROLE_MISMATCH) {
          appStoreActions.setForceLogoutWithMessage(error);
          return;
        }
        showErrorToast(error);
        return;
      }

      // Set role from response to cookie
      if (data?.role) {
        setUserRole(data.role);
        setAuthorized(true);
      } else {
        // If no role from BE, sign out user
        await handleSignOut();
        return;
      }
    } catch (error) {
      if (error === AUTH_ERROR_MESSAGES.ROLE_MISMATCH) {
        appStoreActions.setForceLogoutWithMessage(error);
        return;
      }
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }

    // Don't need to add "getToken" to the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSignOut, setAuthorized]);
  useEffect(() => {
    if (isFirebaseLoading) return;
    validateSession();
  }, [validateSession, isFirebaseLoading]);
  const contextValue = useMemo(() => ({
    isLoading,
    handleSignOut
  }), [isLoading, handleSignOut]);
  return <AuthContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="AuthProvider" data-sentry-source-file="auth.tsx">{children}</AuthContext.Provider>;
};

/**
 * Hook to use authentication context
 * @returns Authentication context value
 */
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
export const useAuthActions = () => {
  const {
    handleSignOut
  } = useAuthContext();
  return {
    handleSignOut
  };
};